import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography, CircularProgress, Alert, Chip, InputAdornment,
  IconButton, Tooltip, FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';
import TopNavbar from '../../components/TopNavBar/TopNavBar';
import AdminPortalSideBar from '../../containers/AdminSideBarContainer/AdminSideBarContainer';
import { PageContainer, ContentContainer } from '../AdminPortalPage/AdminPortalPage.styles';
import { getCustomers } from '../../api/api';

function buildBusinessGroups(rows) {
  const primaries = rows.filter(
    c => (c.account_type || 'Individual') === 'Business' && !c.business_owner_id,
  );
  const byOwnerId = new Map();
  for (const p of primaries) {
    const company =
      p.legal_business_name || p.full_name || p.username || 'Unnamed business';
    byOwnerId.set(p.id, { key: p.id, company, synthetic: false, members: [p] });
  }
  const synthetic = [];
  for (const c of rows) {
    if (!c.business_owner_id) continue;
    const g = byOwnerId.get(c.business_owner_id);
    if (g) {
      g.members.push(c);
    } else {
      let syn = synthetic.find(s => s.key === c.business_owner_id);
      if (!syn) {
        syn = {
          key: c.business_owner_id,
          company: c.legal_business_name || 'Business account',
          synthetic: true,
          members: [],
        };
        synthetic.push(syn);
      }
      syn.members.push(c);
    }
  }
  const all = [...byOwnerId.values(), ...synthetic];
  all.sort((a, b) => a.company.localeCompare(b.company, undefined, { sensitivity: 'base' }));
  return all;
}

function primaryBusinessMember(group) {
  const byId = group.members.find(m => m.id === group.key);
  if (byId && !byId.business_owner_id) return byId;
  const primary = group.members.find(m => !m.business_owner_id);
  return primary || group.members[0] || null;
}

export default function CustomerManagementPage() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [accountTypeFilter, setAccountTypeFilter] = useState('all');

  const fetchCustomers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (searchTerm.trim()) params.search = searchTerm.trim();
      const data = await getCustomers(params);
      setCustomers(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message || 'Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => fetchCustomers(), 300);
    return () => clearTimeout(timer);
  }, [fetchCustomers]);

  const filteredFlat = useMemo(() => {
    if (accountTypeFilter === 'Business') {
      return customers.filter(
        c =>
          (c.account_type || 'Individual') === 'Business' ||
          Boolean(c.business_owner_id),
      );
    }
    if (accountTypeFilter === 'Individual') {
      return customers.filter(
        c =>
          (c.account_type || 'Individual') === 'Individual' &&
          !c.business_owner_id,
      );
    }
    return customers;
  }, [customers, accountTypeFilter]);

  const businessGroups = useMemo(() => {
    if (accountTypeFilter !== 'Business') return null;
    return buildBusinessGroups(filteredFlat);
  }, [filteredFlat, accountTypeFilter]);

  /** "All": flat individuals + grouped business accounts (by company), sorted by display name. */
  const allViewItems = useMemo(() => {
    if (accountTypeFilter !== 'all') return null;
    const bizRows = customers.filter(
      c =>
        (c.account_type || 'Individual') === 'Business' ||
        Boolean(c.business_owner_id),
    );
    const groups = buildBusinessGroups(bizRows);
    const indUsers = customers.filter(
      c =>
        (c.account_type || 'Individual') === 'Individual' &&
        !c.business_owner_id,
    );
    const items = [
      ...indUsers.map(u => ({ type: 'user', user: u })),
      ...groups.map(g => ({ type: 'group', group: g })),
    ];
    const sortKey = item =>
      item.type === 'user'
        ? (item.user.full_name || item.user.username || '').toLowerCase()
        : (item.group.company || '').toLowerCase();
    items.sort((a, b) =>
      sortKey(a).localeCompare(sortKey(b), undefined, { sensitivity: 'base' }),
    );
    return items;
  }, [customers, accountTypeFilter]);

  const summaryText = useMemo(() => {
    if (accountTypeFilter === 'Business' && businessGroups) {
      const nBiz = businessGroups.length;
      return `Total: ${nBiz} business${nBiz !== 1 ? 'es' : ''}`;
    }
    const n = customers.length;
    return `Total: ${n} customer${n !== 1 ? 's' : ''}`;
  }, [accountTypeFilter, businessGroups, customers.length]);

  const renderBusinessRow = group => {
    const primary = primaryBusinessMember(group);
    const totalOrd = group.members.reduce((s, m) => s + (m.total_orders || 0), 0);
    const activeOrd = group.members.reduce((s, m) => s + (m.active_orders || 0), 0);
    const ownerId = group.key;
    const active = primary ? primary.is_active !== false : true;
    return (
      <TableRow
        key={ownerId}
        hover
        sx={{ cursor: 'pointer' }}
        onClick={() => navigate(`/customer-management/${ownerId}`)}
      >
        <TableCell>{group.company}</TableCell>
        <TableCell>{primary?.email || '—'}</TableCell>
        <TableCell align="center">Business</TableCell>
        <TableCell align="center">{totalOrd}</TableCell>
        <TableCell align="center">{activeOrd}</TableCell>
        <TableCell align="center">
          <Chip label={active ? 'Active' : 'Frozen'} color={active ? 'success' : 'error'} size="small" />
        </TableCell>
        <TableCell align="center" onClick={e => e.stopPropagation()}>
          <Tooltip title="View details (primary account)">
            <IconButton size="small" onClick={() => navigate(`/customer-management/${ownerId}`)}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <>
      <TopNavbar />
      <PageContainer>
        <AdminPortalSideBar />
        <ContentContainer>
          <Typography variant="h4" sx={{ mb: 2, fontWeight: 'bold' }}>
            Customer Management
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
              sx={{ width: 350 }}
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} label="Status" onChange={e => setStatusFilter(e.target.value)}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="frozen">Frozen</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Account Type</InputLabel>
              <Select value={accountTypeFilter} label="Account Type" onChange={e => setAccountTypeFilter(e.target.value)}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="Individual">Individual</MenuItem>
                <MenuItem value="Business">Business</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {summaryText}
            </Typography>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Account Type</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Total Orders</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Active Orders</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredFlat.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">No customers found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : accountTypeFilter === 'Business' && businessGroups ? (
                    businessGroups.map(renderBusinessRow)
                  ) : accountTypeFilter === 'all' && allViewItems ? (
                    allViewItems.map(item =>
                      item.type === 'group' ? (
                        renderBusinessRow(item.group)
                      ) : (
                        <TableRow
                          key={item.user.id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/customer-management/${item.user.id}`)}
                        >
                          <TableCell>{item.user.full_name || item.user.username || '—'}</TableCell>
                          <TableCell>{item.user.email}</TableCell>
                          <TableCell align="center">{item.user.account_type || 'Individual'}</TableCell>
                          <TableCell align="center">{item.user.total_orders || 0}</TableCell>
                          <TableCell align="center">{item.user.active_orders || 0}</TableCell>
                          <TableCell align="center">
                            <Chip
                              label={item.user.is_active !== false ? 'Active' : 'Frozen'}
                              color={item.user.is_active !== false ? 'success' : 'error'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="center" onClick={e => e.stopPropagation()}>
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/customer-management/${item.user.id}`)}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ),
                    )
                  ) : (
                    filteredFlat.map(c => (
                      <TableRow
                        key={c.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/customer-management/${c.id}`)}
                      >
                        <TableCell>{c.full_name || c.username || '—'}</TableCell>
                        <TableCell>{c.email}</TableCell>
                        <TableCell align="center">{c.account_type || 'Individual'}</TableCell>
                        <TableCell align="center">{c.total_orders || 0}</TableCell>
                        <TableCell align="center">{c.active_orders || 0}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={c.is_active !== false ? 'Active' : 'Frozen'}
                            color={c.is_active !== false ? 'success' : 'error'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center" onClick={e => e.stopPropagation()}>
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => navigate(`/customer-management/${c.id}`)}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </ContentContainer>
      </PageContainer>
    </>
  );
}
